(() => {
  const ASSET_URL = window.theme?.routes?.automaticDiscounts;
  const PLACEHOLDER_PATTERN = /\{\{\s*(\w+)\s*\}\}/;

  let discountMapPromise = null;
  let discountMap = null;

  function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
    if (!Number.isFinite(number)) return '';

    const fixed = number.toFixed(precision);
    const [dollars, cents] = fixed.split('.');
    const formattedDollars = dollars.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);

    return cents ? `${formattedDollars}${decimal}${cents}` : formattedDollars;
  }

  function formatMoney(cents) {
    const amount = Number(cents);
    const format = window.theme?.settings?.moneyFormat || window.theme?.settings?.moneyWithCurrencyFormat || '${{amount}}';
    const value = amount / 100;

    const placeholder = format.match(PLACEHOLDER_PATTERN)?.[1] || 'amount';
    const formatted = {
      amount: formatWithDelimiters(value, 2),
      amount_no_decimals: formatWithDelimiters(value, 0),
      amount_with_comma_separator: formatWithDelimiters(value, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatWithDelimiters(value, 0, '.', ','),
      amount_with_apostrophe_separator: formatWithDelimiters(value, 2, "'", '.'),
    }[placeholder] || formatWithDelimiters(value, 2);

    return format.replace(PLACEHOLDER_PATTERN, formatted).replace(/[.,]00(?!\d)/, '');
  }

  function formatPercent(value) {
    const percent = Number(value);
    if (!Number.isFinite(percent)) return '';

    return Number.isInteger(percent) ? String(percent) : String(percent.toFixed(2)).replace(/0+$/, '').replace(/\.$/, '');
  }

  function getBannerText(discount) {
    if (discount.discount_type === 'percentage') {
      const value = formatPercent(discount.discount_value);
      const template = window.theme?.strings?.automaticDiscountBannerPercentage || 'Clearance: get an extra [value]% in cart';
      return template.replace('[value]', value);
    }

    const amount = formatMoney(discount.discount_value);
    const template = window.theme?.strings?.automaticDiscountBannerFixed || 'Clearance: get an extra [amount] in cart';
    return template.replace('[amount]', amount);
  }

  async function loadDiscountMap() {
    if (!ASSET_URL) return null;
    if (discountMap) return discountMap;
    if (discountMapPromise) return discountMapPromise;

    discountMapPromise = fetch(ASSET_URL, { credentials: 'same-origin' })
      .then((response) => (response.ok ? response.json() : null))
      .then((data) => {
        discountMap = data?.variants || null;
        return discountMap;
      })
      .catch(() => null);

    return discountMapPromise;
  }

  function removeDiscountBanner(priceEl) {
    priceEl.querySelector('.price__automatic-discount-banner')?.remove();
    delete priceEl.dataset.automaticDiscountApplied;
  }

  function applyDiscountBanner(priceEl, discount) {
    if (!priceEl) return;

    removeDiscountBanner(priceEl);

    if (!discount) return;

    const finalPrice = Number(discount.final_price);
    const originalPrice = Number(discount.original_price);

    if (!Number.isFinite(finalPrice) || !Number.isFinite(originalPrice) || finalPrice >= originalPrice) {
      return;
    }

    const priceDefault = priceEl.querySelector('.price__default');
    if (!priceDefault) return;

    const banner = document.createElement('div');
    banner.className = 'price__automatic-discount-banner';
    banner.setAttribute('role', 'note');
    banner.innerHTML = `
      <span class="price__automatic-discount-kicker">${window.theme?.strings?.automaticDiscountBannerKicker || 'Clearance'}</span>
      <span class="price__automatic-discount-content">
        <span class="price__automatic-discount-text">${getBannerText(discount)}</span>
        <span class="price__automatic-discount-subtext">${window.theme?.strings?.automaticDiscountBannerCartLine || 'when you add to cart.'}</span>
        <span class="price__automatic-discount-caption">${window.theme?.strings?.automaticDiscountBannerCaption || '★ Limited quantity while supplies last'}</span>
      </span>
    `;

    priceDefault.insertAdjacentElement('afterend', banner);
    priceEl.dataset.automaticDiscountApplied = 'true';

    window.requestAnimationFrame(() => {
      banner.classList.add('price__automatic-discount-banner--attention');
    });
  }

  async function apply(root = document) {
    const variants = await loadDiscountMap();
    if (!variants) return;

    const priceEls = root.matches?.('.price[data-variant-id]')
      ? [root]
      : [...root.querySelectorAll?.('.price[data-variant-id]') || []];

    priceEls.forEach((priceEl) => {
      const discount = variants[priceEl.dataset.variantId];
      applyDiscountBanner(priceEl, discount);
    });
  }

  window.GoPiscineAutomaticDiscounts = {
    apply,
    formatMoney,
  };

  document.addEventListener('DOMContentLoaded', () => apply());
  document.addEventListener('on:variant:change', (event) => {
    const section = event.target?.closest?.('.js-product');
    const priceEl = section?.querySelector?.('.product-info__price > .price');
    if (priceEl && event.detail?.variant?.id) {
      priceEl.dataset.variantId = String(event.detail.variant.id);
      apply(priceEl);
    }
  });
  document.addEventListener('on:facet-filters:updated', () => apply());
  document.addEventListener('shopify:section:load', (event) => apply(event.target));
  document.addEventListener('shopify:section:reorder', () => apply());
  document.addEventListener('shopify:section:select', (event) => apply(event.target));
})();
